import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Calendar, Plus, Clock } from 'lucide-react';

export default function Schedules() {
  const [schedules, setSchedules] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    institution_id: '',
    date: '',
    time_start: '',
    time_end: '',
    service_type: '',
    tarif_base: '',
    deplacement_cost: '0'
  });
  const { axiosInstance, user } = useAuth();

  useEffect(() => {
    fetchSchedules();
    fetchInstitutions();
  }, []);

  const fetchSchedules = async () => {
    try {
      const response = await axiosInstance.get('/schedules');
      setSchedules(response.data);
    } catch (error) {
      toast.error('Échec du chargement des horaires');
    } finally {
      setLoading(false);
    }
  };

  const fetchInstitutions = async () => {
    try {
      const response = await axiosInstance.get('/institutions');
      setInstitutions(response.data);
    } catch (error) {
      console.error('Failed to fetch institutions');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post('/schedules', {
        ...formData,
        tarif_base: parseFloat(formData.tarif_base),
        deplacement_cost: parseFloat(formData.deplacement_cost)
      });
      toast.success('Horaire créé');
      setOpen(false);
      setFormData({
        institution_id: '',
        date: '',
        time_start: '',
        time_end: '',
        service_type: '',
        tarif_base: '',
        deplacement_cost: '0'
      });
      fetchSchedules();
    } catch (error) {
      toast.error('Échec de la création');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      available: 'bg-blue-500 text-white',
      booked: 'bg-orange-500 text-white',
      completed: 'bg-green-500 text-white',
      cancelled: 'bg-red-500 text-white'
    };
    const labels = {
      available: 'Disponible',
      booked: 'Réservé',
      completed: 'Complété',
      cancelled: 'Annulé'
    };
    return <Badge className={variants[status]}>{labels[status]}</Badge>;
  };

  if (loading) {
    return (
      <Layout title="Horaires & Disponibilités">
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Horaires & Disponibilités">
      <div className="space-y-6">
        {/* Add Button */}
        <div className="flex justify-end">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="btn-gold" data-testid="add-schedule-button">
                <Plus size={20} className="mr-2" />
                Ajouter horaire
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-2xl" data-testid="schedule-dialog">
              <DialogHeader>
                <DialogTitle className="text-amber-900">Nouvel horaire</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="institution_id">Établissement</Label>
                  <Select
                    value={formData.institution_id}
                    onValueChange={(value) => setFormData({ ...formData, institution_id: value })}
                  >
                    <SelectTrigger data-testid="schedule-institution-select">
                      <SelectValue placeholder="Sélectionner un établissement" />
                    </SelectTrigger>
                    <SelectContent>
                      {institutions.map(inst => (
                        <SelectItem key={inst.id} value={inst.id}>{inst.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date">Date</Label>
                    <Input
                      id="date"
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                      data-testid="schedule-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="service_type">Type de service</Label>
                    <Input
                      id="service_type"
                      value={formData.service_type}
                      onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                      required
                      data-testid="schedule-service-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="time_start">Heure début</Label>
                    <Input
                      id="time_start"
                      type="time"
                      value={formData.time_start}
                      onChange={(e) => setFormData({ ...formData, time_start: e.target.value })}
                      required
                      data-testid="schedule-start-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="time_end">Heure fin</Label>
                    <Input
                      id="time_end"
                      type="time"
                      value={formData.time_end}
                      onChange={(e) => setFormData({ ...formData, time_end: e.target.value })}
                      required
                      data-testid="schedule-end-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="tarif_base">Tarif de base (€)</Label>
                    <Input
                      id="tarif_base"
                      type="number"
                      step="0.01"
                      value={formData.tarif_base}
                      onChange={(e) => setFormData({ ...formData, tarif_base: e.target.value })}
                      required
                      data-testid="schedule-tarif-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="deplacement_cost">Coût déplacement (€)</Label>
                    <Input
                      id="deplacement_cost"
                      type="number"
                      step="0.01"
                      value={formData.deplacement_cost}
                      onChange={(e) => setFormData({ ...formData, deplacement_cost: e.target.value })}
                      data-testid="schedule-deplacement-input"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full btn-gold" data-testid="submit-schedule-button">
                  Créer
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Schedules List */}
        <div className="space-y-4" data-testid="schedules-list">
          {schedules.length === 0 ? (
            <Card className="glass-card border-amber-200">
              <CardContent className="py-12 text-center text-gray-500">
                Aucun horaire trouvé
              </CardContent>
            </Card>
          ) : (
            schedules.map((schedule) => {
              const institution = institutions.find(i => i.id === schedule.institution_id);
              return (
                <Card key={schedule.id} className="glass-card border-amber-200" data-testid={`schedule-card-${schedule.id}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-amber-900" data-testid={`schedule-service-${schedule.id}`}>
                            {schedule.service_type}
                          </h3>
                          {getStatusBadge(schedule.status)}
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <Calendar size={16} className="mr-2" />
                          <span>{schedule.date}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <Clock size={16} className="mr-2" />
                          <span>{schedule.time_start} - {schedule.time_end}</span>
                        </div>
                        {institution && (
                          <p className="text-sm text-gray-600">🏥 {institution.name}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-amber-700">
                          {(schedule.tarif_base + schedule.deplacement_cost).toFixed(2)} €
                        </p>
                        <p className="text-xs text-gray-500">Base: {schedule.tarif_base}€ + Dépl: {schedule.deplacement_cost}€</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </Layout>
  );
}